import { Outlet, useNavigate } from '@tanstack/react-router';
import { useCallback, useEffect, useState } from 'react';
import { Menu } from 'lucide-react';
import { Sidebar } from '@/components/Sidebar';
import { CommandPalette } from '@/components/CommandPalette';
import { useAuth } from '@/hooks/useAuth';
import { useWorkshopProfile } from '@/hooks/useSettings';
import { useKeyboardShortcuts } from '@/hooks/useKeyboardShortcuts';
import { OnboardingWizard } from '@/components/onboarding/OnboardingWizard';
import { DemoBanner } from '@/components/DemoBanner';
import { ImpersonationBanner } from '@/components/ImpersonationBanner';

export function AppLayout() {
  const { user, loading, needsRegistration, isDemo } = useAuth();
  const navigate = useNavigate();
  const { profile, isLoading: profileLoading } = useWorkshopProfile();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const closeMobileNav = useCallback(() => setMobileNavOpen(false), []);
  useKeyboardShortcuts();

  // Close the mobile drawer on Escape.
  useEffect(() => {
    if (!mobileNavOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMobileNavOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [mobileNavOpen]);

  useEffect(() => {
    // F25 hotfix: tambem redireciona quando user existe mas nao tem workshop
    // (needsRegistration=true). Sem isso, user b2c sem workshop entrava
    // direto em /dashboard, AppLayout renderizava com user truthy e os
    // endpoints internos retornavam 400 sem mostrar o form de criar
    // workshop (o form mora em /login).
    if (!loading && (!user || needsRegistration)) {
      navigate({ to: '/login' });
    }
  }, [loading, user, needsRegistration, navigate]);

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  if (!user || needsRegistration) return null;

  // A sessão demo é read-only: o onboarding não consegue salvar (403), então
  // ficaria preso no wizard. O demo já entra direto no dashboard.
  const showOnboarding =
    !isDemo &&
    !profileLoading &&
    profile &&
    user.role === 'manager' &&
    profile.settings.onboarding_completed !== true;

  return (
    <div className="flex h-screen">
      {/* Mobile drawer backdrop — click outside to close */}
      {mobileNavOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 md:hidden"
          onClick={closeMobileNav}
          aria-hidden="true"
        />
      )}
      <Sidebar mobileOpen={mobileNavOpen} onMobileClose={closeMobileNav} />
      <div className="flex flex-1 flex-col overflow-hidden">
        {/* Mobile top bar with hamburger (hidden on >= md) */}
        <div className="flex items-center gap-3 border-b border-border bg-card px-4 py-2 md:hidden">
          <button
            type="button"
            onClick={() => setMobileNavOpen(true)}
            aria-label="Abrir menu"
            className="rounded-md p-2 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          >
            <Menu size={20} />
          </button>
          <span className="text-sm font-bold tracking-wide">MustAuto</span>
        </div>
        <ImpersonationBanner />
        <DemoBanner />
        <main className="flex-1 overflow-y-auto bg-muted p-6">
          <Outlet />
        </main>
      </div>
      {showOnboarding && <OnboardingWizard />}
      <CommandPalette />
    </div>
  );
}
